package com.sddamico.components.motion

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import kotlin.js.json
import kotlin.math.abs

private const val FLIP_ATTR = "data-flip-id"

/** Tags an element whose position [FlipReorder] should animate when it moves. */
fun Modifier.flipId(id: String): Modifier = attrsModifier { attr(FLIP_ATTR, id) }

/**
 * When [key] changes, slides every [flipId]-tagged element inside [container] from its old position to its new one,
 * so a re-sort shows where each item went instead of teleporting it.
 *
 * Positions are read during composition, while the DOM still holds the old order, and the animation starts once the
 * new order has been applied.
 */
@Composable
fun FlipReorder(key: Any, container: HTMLElement?) {
    val reducedMotion = rememberReducedMotion()
    val before = remember(key) { container?.let(::flipPositions) }
    DisposableEffect(key) {
        if (container != null && before != null && !reducedMotion) {
            val after = flipPositions(container)
            container.querySelectorAll("[$FLIP_ATTR]").asList().forEach { node ->
                val element = node as HTMLElement
                val id = element.getAttribute(FLIP_ATTR) ?: return@forEach
                val from = before[id] ?: return@forEach
                val to = after[id] ?: return@forEach
                val dy = from - to
                if (abs(dy) < 0.5) return@forEach
                element.asDynamic().animate(
                    arrayOf(json("transform" to "translateY(${dy}px)"), json("transform" to "none")),
                    json("duration" to Durations.MOVE, "easing" to Ease.InOut.css),
                )
            }
        }
        onDispose { }
    }
}

private fun flipPositions(container: HTMLElement): Map<String, Double> =
    container.querySelectorAll("[$FLIP_ATTR]").asList().associate { node ->
        val element = node as HTMLElement
        element.getAttribute(FLIP_ATTR)!! to element.getBoundingClientRect().top
    }
